import { useCallback, useEffect, useRef, useState } from 'react';
import { ChannelEnum, DeviceTypeEnum, TemplateEnum } from '@ibk/shared-types';
import { isValidCpf } from '@ibk/shared-utils';
import { useLazyHasAccountQuery, useSaveDeviceMutation, useSendEmailTokenMutation, useSendPhoneTokenMutation, } from '../../../../Onboarding/services';
import { useResetPasswordData } from '../../../hooks';
export const RESET_PASSWORD_NO_ACCESS_TOAST_KEY = 'reset_password_show_no_access_toast';
export function useChooseMethod({ onContinue, onRedirectToOnboarding }) {
    const { cpf: persistedCpf, method: persistedMethod, accountData: persistedAccountData, setCpf: setPersistedCpf, setMethod, setAccount, setDeviceId, } = useResetPasswordData();
    const [cpf, setCpf] = useState(persistedCpf ?? '');
    const [cpfTouched, setCpfTouched] = useState(false);
    const [selectedMethod, setSelectedMethod] = useState(persistedMethod ?? 'email');
    const [accountData, setAccountData] = useState(persistedAccountData);
    const [hasAccess, setHasAccess] = useState(persistedAccountData ? true : null);
    const [isSendingToken, setIsSendingToken] = useState(false);
    const [hasAccount, { data, isLoading: isLoadingAccount, error }] = useLazyHasAccountQuery();
    const [saveDevice] = useSaveDeviceMutation();
    const [sendEmailToken] = useSendEmailTokenMutation();
    const [sendPhoneToken] = useSendPhoneTokenMutation();
    const lastCpfRef = useRef('');
    const lastProcessedAccountIdRef = useRef(undefined);
    const cleanCpf = cpf.replace(/\D/g, '');
    const isCpfValid = cleanCpf.length === 11 && isValidCpf(cpf);
    useEffect(() => {
        if (cleanCpf.length === 11 && isValidCpf(cpf) && cleanCpf !== lastCpfRef.current) {
            lastCpfRef.current = cleanCpf;
            setAccountData(null);
            setHasAccess(null);
            lastProcessedAccountIdRef.current = undefined;
            hasAccount({ cpf: cleanCpf });
        }
    }, [cpf, cleanCpf, hasAccount]);
    useEffect(() => {
        if (!data?.result) {
            return;
        }
        let apiAccount;
        if (Array.isArray(data.result)) {
            if (data.result.length === 0) {
                return;
            }
            apiAccount = data.result[0];
        }
        else {
            apiAccount = data.result;
        }
        if (!apiAccount || typeof apiAccount !== 'object' || apiAccount === null) {
            return;
        }
        const account = apiAccount;
        const accountId = account.id ?? account.email;
        const accountKey = accountId ?? JSON.stringify(account);
        if (accountKey && accountKey !== lastProcessedAccountIdRef.current) {
            lastProcessedAccountIdRef.current = accountKey;
            const accountHasAccess = account.has_access ?? false;
            setHasAccess(accountHasAccess);
            if (!accountHasAccess) {
                sessionStorage.setItem(RESET_PASSWORD_NO_ACCESS_TOAST_KEY, 'true');
                setCpf('');
                onRedirectToOnboarding();
                return;
            }
            const phoneNumber = account.phoneNumber ??
                account.phone_number ??
                account.phone ??
                '';
            const email = account.email ??
                account.email_address ??
                account.emailAddress ??
                '';
            const transformedAccount = {
                ...account,
                email,
                phoneNumber,
            };
            setAccountData(transformedAccount);
        }
    }, [data, onRedirectToOnboarding]);
    const hasValidAccount = Boolean(data?.result && Array.isArray(data.result) && data.result.length > 0);
    const showAccountError = isCpfValid && !isLoadingAccount && data !== undefined && !hasValidAccount;
    const cpfInvalidError = cpfTouched && cpf && !isValidCpf(cpf) ? 'reset-password.cpf-invalid' : undefined;
    const accountError = showAccountError ? 'reset-password.cpf-not-found' : undefined;
    const cpfError = accountError ?? cpfInvalidError;
    const isLoading = isLoadingAccount || isSendingToken;
    const canContinue = Boolean(isCpfValid && !isLoading && hasValidAccount && accountData && selectedMethod && !error && hasAccess === true);
    const handleSaveDeviceAndSendToken = useCallback(async () => {
        const cleanDocumentNumber = cpf.replace(/\D/g, '');
        const { getPublicKey, getBrowserInfo, setDeviceIdInSession } = await import('@ibk/shared-auth');
        const publicKey = await getPublicKey();
        const { browserName, osName, osVersion } = getBrowserInfo();
        const channel = selectedMethod === 'email' ? ChannelEnum.email : ChannelEnum.phone;
        const deviceProps = {
            type: DeviceTypeEnum.phone,
            publicKey,
            make: osName,
            model: browserName,
            osVersion,
            channel,
            templateId: TemplateEnum.RESET_PASSWORD,
            documentNumber: cleanDocumentNumber,
        };
        const result = await saveDevice(deviceProps).unwrap();
        if (result?.result?.id) {
            const newDeviceId = result.result.id;
            setDeviceId(newDeviceId);
            setDeviceIdInSession(newDeviceId);
            return newDeviceId;
        }
        return null;
    }, [cpf, selectedMethod, saveDevice, setDeviceId]);
    const handleSendTokenWithExistingDevice = useCallback(async (existingDeviceId) => {
        const cleanDocumentNumber = cpf.replace(/\D/g, '');
        try {
            const channel = selectedMethod === 'email' ? ChannelEnum.email : ChannelEnum.phone;
            const sendToken = selectedMethod === 'email' ? sendEmailToken : sendPhoneToken;
            await sendToken({
                deviceId: existingDeviceId,
                templateId: TemplateEnum.RESET_PASSWORD,
                documentNumber: cleanDocumentNumber,
                channel,
            }).unwrap();
            setDeviceId(existingDeviceId);
            return true;
        }
        catch {
            return false;
        }
    }, [cpf, selectedMethod, sendEmailToken, sendPhoneToken, setDeviceId]);
    const handleContinue = useCallback(async () => {
        if (!canContinue || !accountData) {
            return;
        }
        setIsSendingToken(true);
        try {
            setPersistedCpf(cpf);
            setMethod(selectedMethod);
            setAccount(accountData);
            const { getDeviceIdFromSession } = await import('@ibk/shared-auth');
            const existingDeviceId = getDeviceIdFromSession();
            let tokenSent = false;
            if (existingDeviceId) {
                tokenSent = await handleSendTokenWithExistingDevice(existingDeviceId);
            }
            if (!tokenSent) {
                await handleSaveDeviceAndSendToken();
            }
            onContinue(cpf, selectedMethod, accountData);
        }
        catch {
        }
        finally {
            setIsSendingToken(false);
        }
    }, [
        canContinue,
        accountData,
        cpf,
        selectedMethod,
        setPersistedCpf,
        setMethod,
        setAccount,
        handleSendTokenWithExistingDevice,
        handleSaveDeviceAndSendToken,
        onContinue,
    ]);
    return {
        cpf,
        setCpf,
        cpfTouched,
        setCpfTouched,
        selectedMethod,
        setSelectedMethod,
        isLoading,
        isValid: isCpfValid,
        canContinue,
        cpfError,
        accountError,
        handleContinue,
    };
}
